import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import classNames from "classnames";
import { IoClose, IoMenu, IoGameController } from "react-icons/io5";
import { MdOutlineShoppingBag, MdMoreVert } from "react-icons/md";
import { RiVipDiamondLine } from "react-icons/ri";
import { PiTreasureChest } from "react-icons/pi";
import { FaGift, FaPlay, FaSearch, FaUtensils } from "react-icons/fa";
import { BsPeopleFill, BsShieldFill } from "react-icons/bs";
import { HiLightBulb } from "react-icons/hi";
import { IStore } from "@/redux/defaultStore";
import { toggleLoginOpen } from "@/redux/meta/metaActions";
import DevourPlayIcon from "@/pngs/devourplay-icon.png";
import DevourPlayLogo from "@/pngs/devour-header-logo.png";
import "./DevourPlayNavigation.scss";

interface DevourPlayNavigationProps {
    className?: string;
}

const DevourPlayNavigation: React.FC<DevourPlayNavigationProps> = ({ className }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch();

    const { fullToken, currentUser } = useSelector((state: IStore) => ({
        fullToken: state.authStore.fullToken,
        currentUser: state.metaStore.currentUser,
    }));

    // Debug logging
    useEffect(() => {
    // Component initialization - debug logs removed
    }, [location.pathname, currentUser]);

    const navigationItems = [
        {
            label: "Play",
            path: "/overwolf-go-vip",
            icon: IoGameController,
            isActive: (path: string) => path === "/overwolf-go-vip",
        },
        {
            label: "Guilds",
            path: "/play?view=guilds",
            icon: BsShieldFill,
            isActive: (path: string) => path === "/guilds" || path === "/play" && window.location.search.includes("view=guilds"),
        },
        {
            label: "GoVIP",
            path: "/go-vip-dashboard",
            icon: RiVipDiamondLine,
            isActive: (path: string) => path === "/go-vip-dashboard",
        },
        {
            label: "LootShop",
            path: "/lootshop",
            icon: FaGift,
            isActive: (path: string) => path === "/lootshop",
        },
        {
            label: "LootBoxes",
            path: "/devourlootbox",
            icon: PiTreasureChest,
            isActive: (path: string) => path.includes("/devourlootbox"),
        },
        {
            label: "Discover",
            path: "/overwolf-discover",
            icon: HiLightBulb,
            isActive: (path: string) => path.includes("/overwolf-discover"),
        },
    ];

    const handleNavigation = (path: string) => {
    // Preserve all search parameters when navigating
        const currentParams = new URLSearchParams(searchParams);
        // Navigation handling - debug logs removed

        // Navigate with search params preserved
        navigate({
            pathname: path,
            search: currentParams.toString() ? `?${currentParams.toString()}` : "",
        });
    };

    const handleLoginClick = () => {
        dispatch(toggleLoginOpen(true));
    };

    return (
        <header className={classNames("devourplay-navigation", className)}>
            <div className="devourplay-navigation__container">
                {/* Logo */}
                <div className="devourplay-navigation__logo" onClick={() => handleNavigation("/overwolf-discover")}>
                    <img
                        src={DevourPlayLogo}
                        alt="DevourPlay"
                        className="devourplay-navigation__logo-desktop"
                    />
                    <img
                        src={DevourPlayIcon}
                        alt="DevourPlay"
                        className="devourplay-navigation__logo-mobile"
                    />
                </div>

                {/* Desktop Navigation */}
                <div className="devourplay-navigation__desktop">
                    <div className="devourplay-navigation__items">
                        {navigationItems.map((item) =>
                            <button
                                key={item.path}
                                className={classNames("devourplay-navigation__item", {
                                    "devourplay-navigation__item--active": item.isActive(location.pathname),
                                })}
                                onClick={() => handleNavigation(item.path)}
                            >
                                <span className="devourplay-navigation__item-icon">
                                    <item.icon />
                                </span>
                                <span className="devourplay-navigation__item-text">
                                    {item.label}
                                </span>
                            </button>)}
                    </div>
                </div>

                {/* Mobile Navigation Bar */}
                <div className="devourplay-navigation__mobile-bar">
                    {navigationItems.map((item) =>
                        <button
                            key={item.path}
                            className={classNames("devourplay-navigation__mobile-bar-item", {
                                "devourplay-navigation__mobile-bar-item--active": item.isActive(location.pathname),
                            })}
                            onClick={() => handleNavigation(item.path)}
                        >
                            <item.icon />
                            <span>{item.label}</span>
                        </button>)}
                </div>

                {/* Controls */}
                <div className="devourplay-navigation__controls">

                    {/* Desktop User Menu */}
                    <div className="devourplay-navigation__desktop-user">
                        {!fullToken &&
              <button
                  className="devourplay-navigation__login-btn"
                  onClick={handleLoginClick}
              >
                  Login
              </button>
                        }
                    </div>

                </div>
            </div>

            {/* Mini Mode (PiP) */}
            <div className="devourplay-navigation__mini">
                <div className="devourplay-navigation__mini-logo">
                    <img
                        src={`${import.meta.env.VITE_CDN_URL}/images/DevourPlayLogoWhite.webp`}
                        alt="DevourPlay"
                    />
                </div>
                <div className="devourplay-navigation__mini-xp">
                    {currentUser ? `${currentUser.xp || 0} XP` : "Login"}
                </div>
            </div>
        </header>
    );
};

export default DevourPlayNavigation;